import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { extractMaintenanceFiles } from "../services";
import { formatExistingJobId } from "../components/MaintenanceImportJobDetails";
import MaintenanceExtractStatusPill, {
  MAINTENANCE_LOG_FEATURE_LABEL,
} from "../components/MaintenanceExtractStatusPill";
import {
  extractWorkOrderFromExcelPreview,
  mapExtractItemToMaintenanceRow,
} from "../components/maintenanceUploadHelpers";
import { useTheme } from "./ThemeContext";

const MaintenanceExtractContext = createContext(null);

function revokeGroupPreviews(groups) {
  (groups || []).forEach((group) => {
    if (group?.previewUrl && String(group.previewUrl).startsWith("blob:")) {
      URL.revokeObjectURL(group.previewUrl);
    }
  });
}

function mapExtractedGroups(localGroups, extractedFiles) {
  return (localGroups || []).map((group, index) => {
    const result = extractedFiles[index] || {};
    const previewWorkOrder =
      group.fileType === "excel"
        ? extractWorkOrderFromExcelPreview(group.excelPreview)
        : "";
    const rows = (result.rows || []).map((row, rowIndex) => {
      const mapped = mapExtractItemToMaintenanceRow(
        row,
        `${index}-${rowIndex}`,
      );
      if (previewWorkOrder) {
        mapped.workOrder = previewWorkOrder;
      }
      return mapped;
    });
    return {
      ...group,
      fileType: result.fileType || group.fileType,
      rows,
      error: result.error || "",
      warning: result.preview?.warning || "",
      verified: false,
      page: 1,
      fileId: result.fileId ?? null,
    };
  });
}

function rowCountsFromGroups(groups) {
  return (groups || []).map((group) =>
    Array.isArray(group?.rows) ? group.rows.length : null,
  );
}

const INITIAL_STATE = {
  status: "idle",
  fileCount: 0,
  jobId: null,
  groups: null,
  sourceGroups: null,
  rowCounts: null,
  error: null,
};

export function MaintenanceExtractProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { colors } = useTheme();
  const [state, setState] = useState(INITIAL_STATE);
  const [uploadMounted, setUploadMounted] = useState(false);
  const requestIdRef = useRef(0);
  const uploadMountedRef = useRef(false);
  const groupsRef = useRef(null);
  const sourceGroupsRef = useRef(null);

  groupsRef.current = state.groups;
  sourceGroupsRef.current = state.sourceGroups;

  const markUploadMounted = useCallback((mounted) => {
    const next = Boolean(mounted);
    uploadMountedRef.current = next;
    setUploadMounted(next);
  }, []);

  const clearExtractState = useCallback((revoke = true) => {
    if (revoke) {
      revokeGroupPreviews(groupsRef.current);
      revokeGroupPreviews(sourceGroupsRef.current);
    }
    groupsRef.current = null;
    sourceGroupsRef.current = null;
    setState(INITIAL_STATE);
  }, []);

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    clearExtractState(true);
  }, [clearExtractState]);

  const cancel = useCallback(() => {
    requestIdRef.current += 1;
    clearExtractState(true);
  }, [clearExtractState]);

  const dismissStatusPill = useCallback(() => {
    if (state.status === "running") return;
    clearExtractState(true);
  }, [clearExtractState, state.status]);

  const consumeForReview = useCallback(() => {
    const groups = groupsRef.current;
    const jobId = state.jobId;
    // Keep blobs for Review via navigate state; do not revoke here.
    groupsRef.current = null;
    sourceGroupsRef.current = null;
    setState(INITIAL_STATE);
    return { groups, jobId };
  }, [state.jobId]);

  const openReviewFromPill = useCallback(() => {
    const groups = groupsRef.current;
    const jobId = state.jobId;
    if (!Array.isArray(groups) || !groups.length) {
      clearExtractState(true);
      return;
    }
    groupsRef.current = null;
    sourceGroupsRef.current = null;
    setState(INITIAL_STATE);
    navigate("/maintenance-upload-review", {
      state: { groups, jobId },
    });
  }, [clearExtractState, navigate, state.jobId]);

  const openUploadFromPill = useCallback(() => {
    clearExtractState(true);
    navigate("/settingsuser", {
      state: {
        tab: "leak-rate",
        showImportJobs: true,
        openMaintenanceUpload: true,
      },
    });
  }, [clearExtractState, navigate]);

  // Auto-dismiss success/error when user is on that job's Review page.
  useEffect(() => {
    if (
      location.pathname !== "/maintenance-upload-review" &&
      location.pathname !== "/maintenance-upload-review/"
    ) {
      return;
    }
    if (state.status !== "success" && state.status !== "error") return;
    clearExtractState(false);
  }, [clearExtractState, location.pathname, state.status]);

  const start = useCallback(
    async (localGroups) => {
      if (!Array.isArray(localGroups) || !localGroups.length) {
        toast.error("Please select at least one file");
        return false;
      }
      if (state.status === "running") {
        toast.error("An extraction is already running");
        return false;
      }

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      revokeGroupPreviews(groupsRef.current);
      revokeGroupPreviews(sourceGroupsRef.current);
      groupsRef.current = null;

      setState({
        status: "running",
        fileCount: localGroups.length,
        jobId: null,
        groups: null,
        sourceGroups: localGroups,
        rowCounts: null,
        error: null,
      });
      sourceGroupsRef.current = localGroups;

      try {
        const response = await extractMaintenanceFiles(
          localGroups.map((group) => group.file),
        );
        if (requestIdRef.current !== requestId) return false;

        const payload = response?.data || response;
        const extractedFiles = payload?.files || [];
        const nextGroups = mapExtractedGroups(localGroups, extractedFiles);
        const nextRowCounts = rowCountsFromGroups(nextGroups);
        const jobId = payload?.jobId ?? null;

        if (payload?.status === false && payload?.message) {
          if (uploadMountedRef.current) {
            toast.error(payload.message);
          }
        }

        groupsRef.current = nextGroups;
        setState({
          status: "success",
          fileCount: localGroups.length,
          jobId,
          groups: nextGroups,
          sourceGroups: localGroups,
          rowCounts: nextRowCounts,
          error: null,
        });
        return true;
      } catch (error) {
        if (requestIdRef.current !== requestId) return false;
        const message =
          error?.response?.data?.data?.message ||
          error?.response?.data?.message ||
          "Extract failed";
        setState({
          status: "error",
          fileCount: localGroups.length,
          jobId: null,
          groups: null,
          sourceGroups: localGroups,
          rowCounts: null,
          error: message,
        });
        // On the upload page the pill is for running only; keep toast there.
        if (uploadMountedRef.current) {
          toast.error(message);
        }
        return false;
      }
    },
    [state.status],
  );

  const value = useMemo(
    () => ({
      ...state,
      start,
      cancel,
      reset,
      consumeForReview,
      markUploadMounted,
    }),
    [state, start, cancel, reset, consumeForReview, markUploadMounted],
  );

  const showRunningPill = state.status === "running" && !uploadMounted;
  const showTerminalPill =
    !uploadMounted &&
    (state.status === "success" || state.status === "error");
  const showPill = showRunningPill || showTerminalPill;

  const singleFileName =
    state.fileCount === 1
      ? state.sourceGroups?.[0]?.fileName ||
        state.sourceGroups?.[0]?.file?.name ||
        ""
      : "";
  const jobCode =
    state.jobId != null && state.jobId !== ""
      ? formatExistingJobId(state.jobId)
      : "";

  return (
    <MaintenanceExtractContext.Provider value={value}>
      {children}
      {showPill ? (
        <MaintenanceExtractStatusPill
          featureLabel={MAINTENANCE_LOG_FEATURE_LABEL}
          status={state.status}
          jobCode={jobCode}
          fileCount={state.fileCount}
          fileName={singleFileName}
          colors={colors}
          onActivate={
            state.status === "success"
              ? openReviewFromPill
              : state.status === "error"
                ? openUploadFromPill
                : undefined
          }
          onDismiss={
            state.status === "success" || state.status === "error"
              ? dismissStatusPill
              : undefined
          }
        />
      ) : null}
    </MaintenanceExtractContext.Provider>
  );
}

export function useMaintenanceExtract() {
  const value = useContext(MaintenanceExtractContext);
  if (!value) {
    throw new Error(
      "useMaintenanceExtract must be used within MaintenanceExtractProvider",
    );
  }
  return value;
}
